/**
 * Ticketing constants (SOUND-493 / epic SOUND-486).
 *
 * Mirrors the backend `sound-api/config/statusMachines.mjs` so the UI can show
 * legal transitions, category-driven colors, and human labels without a round
 * trip. The server remains the source of truth — these only drive presentation
 * and pre-flight validation; every mutation is still validated server-side.
 */

// status -> human label
export const TICKET_STATUS_LABELS = {
  open: "Open",
  in_progress: "In progress",
  waiting: "Waiting",
  resolved: "Resolved",
  declined: "Declined",
  withdrawn: "Withdrawn",
  canceled: "Canceled",
};

// status -> stored statusCategory (drives the pill color family)
export const TICKET_STATUS_CATEGORY = {
  open: "todo",
  in_progress: "in_progress",
  waiting: "in_progress",
  resolved: "done",
  declined: "done",
  withdrawn: "canceled",
  canceled: "canceled",
};

export const TICKET_CATEGORY_LABELS = {
  todo: "Todo",
  in_progress: "In progress",
  done: "Done",
  canceled: "Canceled",
};

// Client-side mirror of TICKET_TRANSITIONS (internal drives the full machine).
export const TICKET_TRANSITIONS = {
  open: ["in_progress", "waiting", "declined", "withdrawn", "canceled"],
  in_progress: ["waiting", "resolved", "declined", "withdrawn", "canceled"],
  waiting: ["in_progress", "resolved", "declined", "withdrawn", "canceled"],
  resolved: ["in_progress"],
  declined: ["in_progress"],
  withdrawn: [],
  canceled: [],
};

// Transitions that should collect an optional reason before commit.
// Statuses that pause for a confirm-with-reason panel before committing.
// `withdrawn` joined the set in SOUND-576: it reaches the `canceled` CATEGORY, so
// it now CASCADES to open sub-tasks, and an irreversible cascading transition
// should never fire on one click — the same reasoning SOUND-618 applied to
// application Approve. This affects the INTERNAL path only; the client path goes
// through WithdrawButton, which is already ConfirmDialog-gated.
export const TICKET_STATUS_NEEDS_REASON = new Set([
  "waiting",
  "declined",
  "canceled",
  "withdrawn",
]);

// The only transition a non-internal (client/partner) caller may make.
export const TICKET_NONINTERNAL_ALLOWED_STATUSES = ["withdrawn"];

export const TICKET_PRIORITY_LABELS = {
  low: "Low",
  normal: "Normal",
  high: "High",
  urgent: "Urgent",
};

export const TICKET_PRIORITY_OPTIONS = ["low", "normal", "high", "urgent"];
export const TICKET_STATUS_OPTIONS = Object.keys(TICKET_STATUS_LABELS);
export const TICKET_CATEGORY_OPTIONS = ["todo", "in_progress", "done", "canceled"];

export const TICKET_STATE_OPTIONS = [
  { value: "", label: "All" },
  { value: "open", label: "Open" },
  { value: "closed", label: "Closed" },
];

// ─── Phase 2 (SOUND-497): entity links + typed relations ────────────────────

// Polymorphic entity-link targets. `linkTo(id)` builds the deep-link to that
// entity's detail page. `scoped` = the backend enforces the entity's company
// must equal the ticket's company (company/mid/application/disputifierEnrollment).
// NOTE: disputifierEnrollment is keyed by midId (backend ENTITY_RESOLVERS), so
// it deep-links to the MID detail page where the enrollment is managed.
export const TICKET_ENTITY_META = {
  company: { label: "Company", linkTo: (id) => `/companies/${id}`, scoped: true },
  mid: { label: "MID", linkTo: (id) => `/mids/${id}`, scoped: true },
  application: { label: "Application", linkTo: (id) => `/mid-applications/${id}`, scoped: true },
  disputifierEnrollment: { label: "Disputifier", linkTo: (id) => `/mids/${id}`, scoped: true },
  processor: { label: "Processor", linkTo: (id) => `/settings/processors/${id}`, scoped: false },
  partner: { label: "Partner", linkTo: (id) => `/companies/${id}`, scoped: false },
};

export const TICKET_ENTITY_TYPES = Object.keys(TICKET_ENTITY_META);

// Entity types offered in the create modal's link picker — the company-scoped
// attachables that live under a chosen client company.
export const TICKET_CREATE_ENTITY_TYPES = ["mid", "application", "disputifierEnrollment"];

// Typed ticket-to-ticket relation edges. Direction convention (load-bearing,
// mirrors backend): { fromTicketId: X, toTicketId: Y, type: "depends_on" } means
// X is BLOCKED BY Y. From a given ticket T's perspective:
//   outgoing depends_on (from=T) → T is blocked by `to`  → "Blocked by"
//   incoming depends_on (to=T)   → `from` is blocked by T → "Blocks"
export const TICKET_RELATION_TYPES = ["depends_on", "relates_to", "duplicates"];

export const TICKET_RELATION_LABELS = {
  depends_on: "Depends on",
  relates_to: "Relates to",
  duplicates: "Duplicates",
};
